import React from 'react';
import { Target, Zap, Shield, Users, BarChart3, Lock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Container } from '../layout/Container';

export const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-red-900 py-12">
      <Container>
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Red Set
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Professional AI security assessment platform designed for autonomous red teaming 
            and comprehensive vulnerability analysis of AI systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <Card variant="red">
            <div className="text-center">
              <Target className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Autonomous Red Teaming</h3>
              <p className="text-gray-700">
                Deploy specialized AI agents that automatically probe target systems 
                for vulnerabilities using sophisticated attack vectors.
              </p>
            </div>
          </Card>

          <Card variant="red">
            <div className="text-center">
              <Zap className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Live Assessment</h3>
              <p className="text-gray-700">
                Real-time vulnerability assessment with immediate scoring across 
                multiple security dimensions including jailbreak, bias, and toxicity risks.
              </p>
            </div>
          </Card>

          <Card variant="red">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Analytics</h3>
              <p className="text-gray-700">
                Track security posture over time with detailed trend analysis, 
                historical reporting, and actionable vulnerability insights.
              </p>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <Card variant="red">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="h-6 w-6 mr-3 text-red-500" />
              Red Team Personas
            </h3>
            <div className="space-y-4">
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-gray-900">Jailbreaker Agent</h4>
                <p className="text-sm text-gray-700">System prompt injection and safety bypass techniques</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-gray-900">Bias Probe Agent</h4>
                <p className="text-sm text-gray-700">Discriminatory response and fairness testing</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-gray-900">Toxicity Inducer</h4>
                <p className="text-sm text-gray-700">Harmful content generation and boundary testing</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-gray-900">Privacy Extractor</h4>
                <p className="text-sm text-gray-700">Information leakage and confidentiality violations</p>
              </div>
              <div className="text-sm text-red-600 font-medium">
                + 8 Advanced Personas with sophisticated attack vectors
              </div>
            </div>
          </Card>

          <Card variant="red">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Users className="h-6 w-6 mr-3 text-red-500" />
              Professional Use Cases
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">AI Security Researchers</h4>
                  <p className="text-sm text-gray-700">Conducting vulnerability assessments and safety evaluations</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Security Teams</h4>
                  <p className="text-sm text-gray-700">Performing adversarial audits and penetration testing</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Compliance Officers</h4>
                  <p className="text-sm text-gray-700">Ensuring responsible AI deployment and regulatory compliance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Organizations</h4>
                  <p className="text-sm text-gray-700">Implementing AI safety protocols and risk management</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Card variant="red">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Adversarial Methodology
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                1
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Agent Deployment</h4>
              <p className="text-sm text-gray-700">Autonomous red team agents with specialized tactics</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                2
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Live Probing</h4>
              <p className="text-sm text-gray-700">Real-time interaction with target AI systems</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                3
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Response Analysis</h4>
              <p className="text-sm text-gray-700">Comprehensive vulnerability assessment</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                4
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Risk Scoring</h4>
              <p className="text-sm text-gray-700">Multi-dimensional security evaluation</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                5
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Actionable Reporting</h4>
              <p className="text-sm text-gray-700">Clear findings for remediation</p>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
};